import { useCallback } from 'react';
import type {
  DatePreset,
  TimeGranularity,
  MetricKey,
  ReportViewMode,
} from '@/types/reports';

/**
 * LocalStorage State Persistence Hook for Reports Page
 *
 * Manages persistent storage of report settings in localStorage.
 * Reports page has its own separate filter persistence from Dashboard/Map
 * to allow independent group/model selections.
 *
 * This hook persists:
 * - reports_groupIds_{userId}: Selected group IDs (JSON array) - Reports-specific
 * - reports_datePreset_{userId}: Date preset string
 * - reports_customDateRange_{userId}: Custom date range (JSON object)
 * - reports_granularity_{userId}: Time granularity string
 * - reports_periodCount_{userId}: Period count number
 * - reports_useFullGroup_{userId}: Boolean flag
 * - reports_enabledMetrics_{userId}: Enabled metrics (JSON array)
 * - reports_viewMode_{userId}: View mode string
 * - reports_lastVisit_{userId}: Timestamp (ms) of last visit
 */

export interface ReportsLocalState {
  // Reports-specific filter selections (separate from Dashboard/Map FilterContext)
  groupIds: string[];
  datePreset: DatePreset;
  customDateRange: { start: Date | null; end: Date | null };
  granularity: TimeGranularity;
  periodCount: number;
  useFullGroup: boolean;
  enabledMetrics: MetricKey[];
  viewMode: ReportViewMode;
}

interface UseReportsLocalStorageReturn {
  /**
   * Get the initial state from localStorage
   * Call this once on component mount to restore saved state
   */
  getInitialState: () => Partial<ReportsLocalState>;

  /**
   * Update localStorage with current filter state
   * Call this when filters change to persist state
   */
  updateLocalState: (state: Partial<ReportsLocalState>) => void;

  /**
   * Check if localStorage has any saved presentation settings
   */
  hasStoredFilters: () => boolean;

  /**
   * Clear all stored report presentation settings
   */
  clearStoredFilters: () => void;

  /**
   * Check if user has seen the onboarding tooltip
   */
  getHasSeenOnboarding: () => boolean;

  /**
   * Mark onboarding as seen (persists to localStorage)
   */
  setHasSeenOnboarding: () => void;

  /**
   * Check if user has seen the compare tab onboarding tooltip
   */
  getHasSeenCompareOnboarding: () => boolean;

  /**
   * Mark compare onboarding as seen (persists to localStorage)
   */
  setHasSeenCompareOnboarding: () => void;
}

const STORAGE_PREFIX = 'reports_';
const VALID_PRESETS: DatePreset[] = [
  'today',
  'yesterday',
  'last-7-days',
  'last-30-days',
  'last-90-days',
  'this-month',
  'last-month',
  'this-quarter',
  'last-quarter',
  'this-year',
  'last-year',
  'custom',
];

const VALID_GRANULARITIES: TimeGranularity[] = ['daily', 'weekly', 'monthly'];
const VALID_METRICS: MetricKey[] = ['solarKwh', 'gridKwh', 'offGridPercent', 'co2Reduced', 'costSaved', 'gridSellRevenue'];
const VALID_VIEW_MODES: ReportViewMode[] = ['charts', 'summary', 'compare'];

/**
 * Generate a user-scoped storage key to prevent cross-user data leakage
 * @param key - The base key name
 * @param userId - The current user's ID (optional for backward compat during migration)
 */
function getStorageKey(key: string, userId?: string): string {
  if (userId) {
    return `${STORAGE_PREFIX}${key}_${userId}`;
  }
  // Fallback for migration - will be cleaned up by migration effect
  return `${STORAGE_PREFIX}${key}`;
}

function safeParseJSON<T>(value: string | null, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

/**
 * Hook for persisting Reports page presentation settings
 * @param userId - Current user ID for scoped storage (optional during migration)
 */
export function useReportsLocalStorage(userId?: string): UseReportsLocalStorageReturn {
  // Get initial state from localStorage
  const getInitialState = useCallback((): Partial<ReportsLocalState> => {
    const state: Partial<ReportsLocalState> = {};

    try {
      // Update last visit timestamp
      if (userId) {
        localStorage.setItem(getStorageKey('lastVisit', userId), new Date().getTime().toString());
      }

      // Parse date preset
      const presetValue = localStorage.getItem(getStorageKey('datePreset', userId));
      if (presetValue && VALID_PRESETS.includes(presetValue as DatePreset)) {
        state.datePreset = presetValue as DatePreset;
      }

      // Parse custom date range
      const dateRangeValue = localStorage.getItem(getStorageKey('customDateRange', userId));
      if (dateRangeValue) {
        const parsed = safeParseJSON<{ start: string | null; end: string | null }>(
          dateRangeValue,
          { start: null, end: null }
        );
        const start = parsed.start ? new Date(parsed.start) : null;
        const end = parsed.end ? new Date(parsed.end) : null;
        if ((start && !isNaN(start.getTime())) || (end && !isNaN(end.getTime()))) {
          state.customDateRange = {
            start: start && !isNaN(start.getTime()) ? start : null,
            end: end && !isNaN(end.getTime()) ? end : null,
          };
        }
      }

      // Parse granularity
      const granularityValue = localStorage.getItem(getStorageKey('granularity', userId));
      if (granularityValue && VALID_GRANULARITIES.includes(granularityValue as TimeGranularity)) {
        state.granularity = granularityValue as TimeGranularity;
      }

      // Parse period count
      const periodCountValue = localStorage.getItem(getStorageKey('periodCount', userId));
      if (periodCountValue) {
        const periodCount = parseInt(periodCountValue, 10);
        if (!isNaN(periodCount) && periodCount > 0 && periodCount <= 52) {
          state.periodCount = periodCount;
        }
      }

      // Parse useFullGroup
      const fullProdValue = localStorage.getItem(getStorageKey('useFullGroup', userId));
      if (fullProdValue === 'true') {
        state.useFullGroup = true;
      }

      // Parse enabled metrics
      const metricsValue = safeParseJSON<string[]>(
        localStorage.getItem(getStorageKey('enabledMetrics', userId)),
        []
      );
      const validMetrics = metricsValue.filter(m => VALID_METRICS.includes(m as MetricKey)) as MetricKey[];
      if (validMetrics.length > 0) {
        state.enabledMetrics = validMetrics;
      }

      // Parse view mode
      const viewModeValue = localStorage.getItem(getStorageKey('viewMode', userId));
      if (viewModeValue && VALID_VIEW_MODES.includes(viewModeValue as ReportViewMode)) {
        state.viewMode = viewModeValue as ReportViewMode;
      }

      // Parse groupIds - Reports-specific group selection
      const groupIdsValue = localStorage.getItem(getStorageKey('groupIds', userId));
      if (groupIdsValue) {
        const parsed = safeParseJSON<string[]>(groupIdsValue, []);
        if (Array.isArray(parsed)) {
          state.groupIds = parsed;
        }
      }
    } catch (error) {
      console.error('[useReportsLocalStorage] Error reading from localStorage:', error);
    }

    return state;
  }, [userId]);

  // Update localStorage with current state
  const updateLocalState = useCallback((state: Partial<ReportsLocalState>) => {
    try {
      // Update date preset
      if (state.datePreset !== undefined) {
        if (state.datePreset !== 'last-30-days') {
          localStorage.setItem(getStorageKey('datePreset', userId), state.datePreset);
        } else {
          localStorage.removeItem(getStorageKey('datePreset', userId));
        }
      }

      // Update custom date range
      if (state.customDateRange !== undefined) {
        if (state.customDateRange.start || state.customDateRange.end) {
          localStorage.setItem(getStorageKey('customDateRange', userId), JSON.stringify({
            start: state.customDateRange.start?.toISOString() || null,
            end: state.customDateRange.end?.toISOString() || null,
          }));
        } else {
          localStorage.removeItem(getStorageKey('customDateRange', userId));
        }
      }

      // Update granularity
      if (state.granularity !== undefined) {
        if (state.granularity !== 'weekly') {
          localStorage.setItem(getStorageKey('granularity', userId), state.granularity);
        } else {
          localStorage.removeItem(getStorageKey('granularity', userId));
        }
      }

      // Update period count
      if (state.periodCount !== undefined) {
        if (state.periodCount !== 4) {
          localStorage.setItem(getStorageKey('periodCount', userId), state.periodCount.toString());
        } else {
          localStorage.removeItem(getStorageKey('periodCount', userId));
        }
      }

      // Update useFullGroup
      if (state.useFullGroup !== undefined) {
        if (state.useFullGroup) {
          localStorage.setItem(getStorageKey('useFullGroup', userId), 'true');
        } else {
          localStorage.removeItem(getStorageKey('useFullGroup', userId));
        }
      }

      // Update enabled metrics
      if (state.enabledMetrics !== undefined) {
        const defaultMetrics = ['solarKwh', 'offGridPercent', 'co2Reduced'];
        const isDefault =
          state.enabledMetrics.length === defaultMetrics.length &&
          defaultMetrics.every(m => state.enabledMetrics!.includes(m as MetricKey));
        if (!isDefault && state.enabledMetrics.length > 0) {
          localStorage.setItem(getStorageKey('enabledMetrics', userId), JSON.stringify(state.enabledMetrics));
        } else {
          localStorage.removeItem(getStorageKey('enabledMetrics', userId));
        }
      }

      // Update view mode
      if (state.viewMode !== undefined) {
        if (state.viewMode !== 'charts') {
          localStorage.setItem(getStorageKey('viewMode', userId), state.viewMode);
        } else {
          localStorage.removeItem(getStorageKey('viewMode', userId));
        }
      }

      // Update groupIds - Reports-specific group selection
      if (state.groupIds !== undefined) {
        if (state.groupIds.length > 0) {
          localStorage.setItem(getStorageKey('groupIds', userId), JSON.stringify(state.groupIds));
        } else {
          localStorage.removeItem(getStorageKey('groupIds', userId));
        }
      }
    } catch (error) {
      console.error('[useReportsLocalStorage] Error writing to localStorage:', error);
    }
  }, [userId]);

  // Check if localStorage has any saved presentation settings
  const hasStoredFilters = useCallback(() => {
    const keys = [
      'datePreset',
      'customDateRange',
      'granularity',
      'periodCount',
      'useFullGroup',
      'enabledMetrics',
      'viewMode',
      'groupIds',
    ];
    return keys.some(key => localStorage.getItem(getStorageKey(key, userId)) !== null);
  }, [userId]);

  // Clear all stored report presentation settings
  const clearStoredFilters = useCallback(() => {
    const keys = [
      'datePreset',
      'customDateRange',
      'granularity',
      'periodCount',
      'useFullGroup',
      'enabledMetrics',
      'viewMode',
      'lastVisit',
      'groupIds',
    ];
    keys.forEach(key => localStorage.removeItem(getStorageKey(key, userId)));
  }, [userId]);

  // Check if user has seen the onboarding tooltip
  const getHasSeenOnboarding = useCallback(() => {
    return localStorage.getItem(getStorageKey('hasSeenOnboarding', userId)) === 'true';
  }, [userId]);

  // Mark onboarding as seen
  const setHasSeenOnboarding = useCallback(() => {
    localStorage.setItem(getStorageKey('hasSeenOnboarding', userId), 'true');
  }, [userId]);

  // Check if user has seen the compare tab onboarding tooltip
  const getHasSeenCompareOnboarding = useCallback(() => {
    return localStorage.getItem(getStorageKey('hasSeenCompareOnboarding', userId)) === 'true';
  }, [userId]);

  // Mark compare onboarding as seen
  const setHasSeenCompareOnboarding = useCallback(() => {
    localStorage.setItem(getStorageKey('hasSeenCompareOnboarding', userId), 'true');
  }, [userId]);

  return {
    getInitialState,
    updateLocalState,
    hasStoredFilters,
    clearStoredFilters,
    getHasSeenOnboarding,
    setHasSeenOnboarding,
    getHasSeenCompareOnboarding,
    setHasSeenCompareOnboarding,
  };
}
